import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ListItemIcon,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  EntityTypes,
  JobType,
  Property,
  PropertyGroup,
  Tenant,
  RevisionQueueMessage,
  DisplayMode,
  FormMode,
  PPIPusherEvent,
  TagStatus,
  TowStatus,
  Job,
} from 'types';
import { ROUTES, routeWithParams } from 'router';
import { PanelRootContext } from 'types';
import {
  useLoaderData,
  useOutletContext,
  useNavigate,
  useRevalidator,
  useSearchParams,
  LoaderFunctionArgs,
  useFetcher,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  capitalize,
  formatDateRangeReportFileName,
  formatLicensePlateString,
  formatShortDateTime,
  formatVehicleString,
  generateCSV,
  generateJobPDF,
  getMostRecentStatus,
  getShortFormattedAddress,
  logError,
  THE_DISTANT_FUTURE,
  canJobBeCancelled,
} from 'utils';
import { AppContext } from 'context/appContext';
import JobTypeChip from 'components/chips/JobTypeChip';

import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_Row,
  MRT_SortingState,
  MRT_RowVirtualizer,
} from 'material-react-table';
import {
  Add,
  Approval,
  DoNotDisturb,
  FileDownload,
  History,
} from '@mui/icons-material';
import {
  useDefaultTableOptions,
  TableType,
} from 'hooks/useDefaultTableOptions';
import dayjs, { Dayjs } from 'dayjs';
import Pusher from 'pusher-js';
import { DatePicker } from '@mui/x-date-pickers';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import MobilePanelList from 'components/lists/MobilePanelList';
import {
  getTenantJobs,
  getPropertyGroupJobs,
  getPropertyJobs,
  getCancelReasons,
  GetCancelReasonsResponse,
} from 'api';
import JobReviewDialog from './JobReviewDialog';
import CancelJobDialog from './CancelJobDialog';
import { JobActionHandler, JobsActionResponse } from './jobsAction';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import AuthService from 'utils/AuthService';

// How far back the jobs grid reaches by default. Must stay in sync with
// defaultJobSearchDays in services/job/api/utils.go, otherwise the date pickers show a
// range that does not match the rows the API returns.
const DEFAULT_SEARCH_DAYS = 30;

export interface JobsPanelContext {
  entity: Property | PropertyGroup | Tenant;
  formMode: FormMode;
  setFormMode: (formMode: FormMode) => void;
  displayMode: DisplayMode;
  onBack: () => void;
}

export interface JobsLoaderData {
  _jobs: Promise<Job[]>;
}

export function jobsLoader({
  request,
  params,
}: LoaderFunctionArgs): JobsLoaderData {
  const tenantID = Number(params.tenantID) || 0;
  const propertyID = Number(params.propertyID) || 0;
  const propertyGroupID = Number(params.groupID) || 0;

  const url = new URL(request.url);
  if (propertyID > 0) {
    return {
      _jobs: getPropertyJobs({
        tenantID,
        propertyID,
        start: url.searchParams.get('start') || '',
        end: url.searchParams.get('end') || '',
      }),
    };
  } else if (propertyGroupID > 0) {
    return {
      _jobs: getPropertyGroupJobs({
        tenantID,
        propertyGroupID,
        start: url.searchParams.get('start') || '',
        end: url.searchParams.get('end') || '',
      }),
    };
  } else if (tenantID > 0) {
    return {
      _jobs: getTenantJobs({
        tenantID,
        start: url.searchParams.get('start') || '',
        end: url.searchParams.get('end') || '',
      }),
    };
  }

  throw new Response('Invalid tenant ID', { status: 400 });
}

export async function cancelJobLoader({
  params,
  request,
}: LoaderFunctionArgs): Promise<GetCancelReasonsResponse> {
  console.log('request:', request);
  console.log('params:', params);
  const tenantID = Number(params.tenantID) || 0;
  const cancelReasons = await getCancelReasons(tenantID);
  if (cancelReasons) {
    return cancelReasons;
  }
  return Promise.resolve({ cancelReasons: [] });
}

export const JobsPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const theme = useTheme();
  const { palette, breakpoints } = theme;
  const displayToast = useContext(ToastContext);
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const cancelReasonsFetcher = useFetcher();
  const navigate = useNavigate();
  const { _jobs } = useLoaderData() as JobsLoaderData;
  const { entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup | Tenant>>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const revalidator = useRevalidator();
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(DEFAULT_SEARCH_DAYS, 'day'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showDialog, setShowDialog] = useState<'cancel' | null>(null);
  const canCreate = state.entityPermissions.job.create;
  const canEdit = state.entityPermissions.job.update;
  const canCancelJob =
    !!state.tenant.data?.allowJobCancellationBeforeStatus &&
    state.features.allowJobCancellation;
  // await data fetch
  useEffect(() => {
    const awaitLoaderData = async () => {
      const j = await _jobs;
      setJobs(j);
      setIsLoading(false);
    };
    awaitLoaderData();
  }, [_jobs]);

  // scroll virtualization
  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      if (error instanceof Error) {
        logError(error);
      } else {
        logError(new Error(String(error)));
      }
    }
  }, [sorting]);

  // pusher
  useEffect(() => {
    const pusher = new Pusher(window.PUSHER_KEY, {
      cluster: window.PUSHER_CLUSTER,
    });
    const channel = pusher.subscribe(`tenant-${state.tenant.id}`);
    channel.bind(PPIPusherEvent.JobCreated, (e: RevisionQueueMessage<Job>) => {
      if (e.userID !== state.user.id) {
        revalidator.revalidate();
      }
    });
    channel.bind(PPIPusherEvent.JobUpdated, (e: RevisionQueueMessage<Job>) => {
      if (e.userID !== state.user.id) {
        //todo:  check if update is a location update.  if so, don't revalidate, just update the location
        revalidator.revalidate();
      }
    });
    return () => {
      channel.unbind(PPIPusherEvent.JobCreated);
      channel.unbind(PPIPusherEvent.JobUpdated);
      pusher.unsubscribe(`tenant-${state.tenant.id}`);
    };
  }, []);

  // toast handling
  useEffect(() => {
    if (fetcher.data) {
      const response = fetcher.data as JobsActionResponse;
      if (response.error) {
        displayToast({
          message: getErrorMessage(response),
          type: TOAST_TYPE.ERROR,
        });
      } else {
        const message = getSuccessMessage(response);
        displayToast({
          message,
          type: TOAST_TYPE.SUCCESS,
        });
        setSelectedJob(null);
        setShowDialog(null);
      }
    }
  }, [fetcher.data]);

  const getSuccessMessage = useCallback(
    (response: JobsActionResponse) => {
      switch (response.handler) {
        case JobActionHandler.APPROVE:
          return t('approve_success', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
          });
        case JobActionHandler.REJECT:
          return t('deny_success', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
          });
        case JobActionHandler.CANCEL:
          return t('cancel_success', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
          });
        default:
          return '';
      }
    },
    [t],
  );
  const getErrorMessage = useCallback(
    (response: JobsActionResponse) => {
      switch (response.handler) {
        case JobActionHandler.APPROVE:
          return t('approve_error', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
          });
        case JobActionHandler.REJECT:
          return t('deny_error', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
          });
        case JobActionHandler.CANCEL:
          return t('cancel_error', {
            ns: 'toasts',
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
          });
        default:
          return '';
      }
    },
    [t],
  );

  const handleStartDateChange = useCallback(
    (date: Dayjs | null) => {
      setStartDate(date);
      setSearchParams((prev: URLSearchParams) => {
        prev.set('start', date?.toISOString() || '');
        return prev;
      });
    },
    [setSearchParams],
  );
  const handleEndDateChange = useCallback(
    (date: Dayjs | null) => {
      setEndDate(date);
      setSearchParams((prev: URLSearchParams) => {
        prev.set('end', date?.toISOString() || '');
        return prev;
      });
    },
    [setSearchParams],
  );
  const handleRowClick = useCallback(
    (id: number) => {
      if (displayMode === 'property') {
        navigate(
          routeWithParams(ROUTES.PROPERTY_JOBS_DETAILS, [
            state.tenant.id,
            entity.id,
            id,
          ]),
        );
      } else if (displayMode === 'group') {
        navigate(
          routeWithParams(ROUTES.PROPERTY_GROUP_JOBS_DETAILS, [
            state.tenant.id,
            entity.id,
            id,
          ]),
        );
      } else if (displayMode === 'tenant') {
        navigate(routeWithParams(ROUTES.JOBS_DETAILS, [state.tenant.id, id]));
      }
    },
    [displayMode, navigate, state.tenant.id, entity.id],
  );
  const handleCreateClick = useCallback(() => {
    if (displayMode === 'property') {
      navigate(
        routeWithParams(ROUTES.PROPERTY_JOBS_CREATE, [
          state.tenant.id,
          entity.id,
        ]),
      );
    } else if (displayMode === 'group') {
      navigate(
        routeWithParams(ROUTES.PROPERTY_GROUP_JOBS_CREATE, [
          state.tenant.id,
          entity.id,
        ]),
      );
    } else if (displayMode === 'tenant') {
      navigate(routeWithParams(ROUTES.JOBS_CREATE, [state.tenant.id]));
    }
  }, [displayMode, navigate, state.tenant.id, entity.id]);
  const handleReviewJob = useCallback(
    (id: number) => {
      const job = jobs.find((j) => j.id === id);
      if (job) {
        setSelectedJob(job);
      }
    },
    [jobs],
  );
  const handleJobApproval = useCallback(
    (jobID: number) => {
      fetcher.submit(
        {
          handler: JobActionHandler.APPROVE,
          signature: JSON.stringify(state.signature),
          jobID: jobID,
          tenantID: state.tenant.id,
        },
        {
          method: 'PUT',
          encType: 'application/json',
        },
      );
      setSelectedJob(null);
    },
    [fetcher, state.signature, state.tenant.id],
  );
  const handleJobDeny = useCallback(
    (jobID: number, reason: string) => {
      fetcher.submit(
        {
          handler: JobActionHandler.REJECT,
          rejectReason: reason,
          signature: JSON.stringify(state.signature),
          jobID: jobID,
          tenantID: state.tenant.id,
        },
        {
          method: 'PUT',
          encType: 'application/json',
        },
      );
      setSelectedJob(null);
    },
    [fetcher, state.signature, state.tenant.id],
  );
  const handleViewRevisions = useCallback(
    (id: number) => {
      navigate(
        routeWithParams(ROUTES.REVISIONS, [
          state.tenant.id,
          EntityTypes.Job,
          id.toString(),
        ]),
      );
    },
    [navigate, state.tenant.id],
  );
  const handleExportSelectedAsCSV = useCallback(
    async (
      rows: MRT_Row<Job>[],
      startDate: Date,
      endDate: Date | undefined,
    ) => {
      generateCSV(
        rows.map((row) => row.original),
        formatDateRangeReportFileName({
          startDate,
          endDate: endDate || dayjs(new Date(THE_DISTANT_FUTURE)).toDate(),
          entityType: EntityTypes.Job,
          ext: 'csv',
        }),
        'job',
      );
    },
    [],
  );
  const handleExportSelectedAsPDF = useCallback(
    async (
      rows: MRT_Row<Job>[],
      startDate: Date,
      endDate: Date | undefined,
    ) => {
      generateJobPDF(
        rows.map((row) => row.original),
        formatDateRangeReportFileName({
          startDate,
          endDate: endDate || dayjs(new Date(THE_DISTANT_FUTURE)).toDate(),
          entityType: EntityTypes.Job,
          ext: 'pdf',
        }),
        state.tenant,
      );
    },
    [state.tenant],
  );
  const handleExportAllAsCSV = useCallback(async () => {
    generateCSV(
      jobs,
      formatDateRangeReportFileName({
        startDate: dayjs().toDate(),
        endDate: dayjs().toDate(),
        entityType: EntityTypes.Job,
        ext: 'csv',
      }),
      'permit',
    );
  }, [jobs]);
  const handleExportAllAsPDF = useCallback(async () => {
    generateJobPDF(
      jobs,
      formatDateRangeReportFileName({
        startDate: dayjs().toDate(),
        endDate: dayjs().toDate(),
        entityType: EntityTypes.Job,
        ext: 'pdf',
      }),
      state.tenant,
    );
  }, [jobs, state.tenant]);

  const handleCancelJobClick = (jobID: number) => {
    setSelectedJob(jobs.find((j) => j.id === jobID) || null);
    setShowDialog('cancel');
    if (cancelReasonsFetcher.state === 'idle' && !cancelReasonsFetcher.data) {
      cancelReasonsFetcher.load(
        routeWithParams(ROUTES.CANCEL_JOB_REASONS, [state.tenant.id]),
      );
    }
  };

  const columns: MRT_ColumnDef<Job>[] = [
    {
      header: t('job.id', { ns: 'entities' }),
      accessorKey: 'id',
    },
    {
      header: t('job.externalID', { ns: 'entities' }),
      accessorKey: 'externalID',
    },
    {
      header: t('job.property.label', { ns: 'entities' }),
      id: 'property',
      accessorFn: (originalRow) => originalRow.propertyName,
    },
    {
      header: t('job.location.label', { ns: 'entities' }),
      id: 'location',
      accessorFn: (originalRow) =>
        getShortFormattedAddress(originalRow?.location),
    },
    {
      header: t('job.vehicle.label', { ns: 'entities' }),
      id: 'vehicle',
      accessorFn: (row) => formatVehicleString(row?.vehicle),
    },
    {
      header: t('job.status', { ns: 'entities' }),
      id: 'status',
      accessorFn: (row) => getMostRecentStatus(row),
    },
    {
      header: t('job.requestTime', { ns: 'entities' }),
      id: 'requestTime',
      accessorFn: (row) => new Date(row.requestTime),
      Cell: ({ cell }) => formatShortDateTime(cell.getValue<Date>()),
      filterVariant: 'date',
    },
    {
      header: t('job.created', { ns: 'entities' }),
      id: 'created',
      accessorFn: (row) => new Date(row.created),
      Cell: ({ cell }) => formatShortDateTime(cell.getValue<Date>()),
      filterVariant: 'date',
    },
    {
      header: t('job.updated', { ns: 'entities' }),
      id: 'updated',
      accessorFn: (row) => new Date(row.updated),
      Cell: ({ cell }) => formatShortDateTime(cell.getValue<Date>()),
      filterVariant: 'date',
      filterFn: (row, id, filterValue) => {
        return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
      },
    },
    {
      header: t('job.vehicle.plate.label', { ns: 'entities' }),
      id: 'plate.number',
      accessorFn: (originalRow) => formatLicensePlateString(originalRow?.plate),
      enableColumnFilter: false,
    },
    {
      header: t('job.type', { ns: 'entities' }),
      accessorKey: 'type',
      filterVariant: 'select',
      filterSelectOptions: Object.values(JobType).map((type) => ({
        label:
          type === JobType.NONE
            ? t('jobType.none', { ns: 'enums' })
            : t(`jobType.${type}`, { ns: 'enums' }),
        value: type,
      })),
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell }) => (
        // eslint-disable-next-line react/prop-types
        <JobTypeChip type={cell.getValue<JobType>()} size={'medium'} />
      ),
    },
  ];

  const tableDefaults = useDefaultTableOptions<Job>();
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: jobs,
    columns,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableRowSelection: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'comfortable',
      columnVisibility: {
        id: AuthService.IsSuperAdmin(),
        externalID: AuthService.IsSuperAdmin(),
        location: false,
        plate: true,
        vehicle: false,
        updated: true,
        created: false,
        requestTime: true,
        status: true,
        type: true,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row: Job) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        e.stopPropagation();
        handleRowClick(row.original.id);
      },
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        optionsDisabled={!canCreate}
        tableType={TableType.PANEL}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('job.label', { ns: 'entities' }),
            }),
            disabled: !canCreate,
            icon: <Add />,
          },
          {
            onClick: () => handleExportAllAsCSV(),
            content: t('buttons.exportAllCsv'),
            icon: <FileDownload />,
            disabled: jobs.length === 0 || isLoading,
          },
          {
            onClick: () => handleExportAllAsPDF(),
            content: t('buttons.exportAllPdf'),
            icon: <FileDownload />,
            disabled: jobs.length === 0 || isLoading,
          },
          {
            onClick: () => {
              handleExportSelectedAsCSV(
                table.getSelectedRowModel().rows,
                startDate?.toDate() ||
                  dayjs().subtract(DEFAULT_SEARCH_DAYS, 'day').toDate(),
                endDate?.toDate(),
              );
            },
            content: t('buttons.exportSelectedCsv'),
            icon: <FileDownload />,
            disabled:
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected(),
          },
          {
            onClick: () => {
              handleExportSelectedAsPDF(
                table.getSelectedRowModel().rows,
                startDate?.toDate() ||
                  dayjs().subtract(DEFAULT_SEARCH_DAYS, 'day').toDate(),
                endDate?.toDate(),
              );
            },
            content: t('buttons.exportSelectedPdf'),
            icon: <FileDownload />,
            disabled:
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected(),
          },
        ]}
      >
        <Stack
          spacing={1}
          direction={'row'}
          sx={{ px: 2, display: 'flex', alignItems: 'center' }}
        >
          <DatePicker
            disableFuture
            value={startDate}
            onChange={handleStartDateChange}
            views={['year', 'month', 'day']}
            slotProps={{
              textField: {
                color: 'primary',
                placeholder: t('select', { name: t('date') }),
                label: '',
                margin: 'none',
                variant: 'standard',
                sx: {
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '2px solid rgb(144, 144, 144)',
                  height: 42,
                },
                InputProps: {
                  sx: {
                    py: '5px',
                    px: 1,
                  },
                  disableUnderline: true,
                },
              },
            }}
          />
          <Typography variant='caption' color={palette.secondary.contrastText}>
            {t('to')}
          </Typography>
          <DatePicker
            label={''}
            disableFuture
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{
              textField: {
                color: 'primary',
                placeholder: t('select', { name: t('date') }),
                label: '',
                margin: 'none',
                variant: 'standard',
                sx: {
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '2px solid rgb(144, 144, 144)', //todo: we're breaking the onhover, if you have some free time, fix this
                  height: 42,
                },
                InputProps: {
                  sx: {
                    py: '5px',
                    px: 1,
                  },
                  disableUnderline: true,
                },
              },
            }}
          />
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={0}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleReviewJob(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={
            row.original.status !== TagStatus.PENDING_APPROVAL &&
            row.original.status !== TowStatus.PENDING_APPROVAL &&
            !AuthService.IsPM()
          }
        >
          <ListItemIcon>
            <Approval />
          </ListItemIcon>
          {t('dialogs.approval.menu', {
            ns: 'jobs',
            type: capitalize(row.original.type),
          })}
        </MenuItem>,
      ];
      if (canCancelJob && canEdit) {
        options.push(
          <MenuItem
            key={2}
            divider
            disabled={
              !canJobBeCancelled(
                row.original,
                state.tenant.data?.allowJobCancellationBeforeStatus,
              )
            }
            onClick={() => {
              closeMenu();
              handleCancelJobClick(row.original.id);
            }}
            sx={{ m: 0, backgroundColor: 'white' }}
          >
            <ListItemIcon>
              <DoNotDisturb />
            </ListItemIcon>
            {t('dialogs.cancel.header', {
              ns: 'jobs',
              type: capitalize(row.original.type),
            })}
          </MenuItem>,
        );
      }
      return options;
    },
  });

  if (mobile) {
    const itemHeight = 70;
    const renderItem = useCallback(
      (job: Job): ReactNode => (
        <ListItem
          key={job.id}
          divider
          sx={{ height: itemHeight }}
          onClick={() => {
            handleRowClick(job.id);
          }}
        >
          <ListItemText
            primary={job.propertyName}
            secondary={`${capitalize(job.status)}${
              job.plate?.number ? ' - ' : ''
            }${formatLicensePlateString(job.plate)}`}
          />
          <ListItemAvatar>
            <JobTypeChip type={job.type} size={'small'} />
          </ListItemAvatar>
        </ListItem>
      ),
      [handleRowClick],
    );

    return (
      <>
        {selectedJob && showDialog === 'cancel' && (
          <CancelJobDialog
            isOpen={true}
            job={selectedJob}
            handleClose={() => {
              setShowDialog(null);
              setSelectedJob(null);
            }}
            cancelReasons={cancelReasonsFetcher.data?.cancelReasons ?? []}
            fetcherKey={fetcherKey}
          />
        )}
        {selectedJob && showDialog !== 'cancel' && (
          <JobReviewDialog
            isOpen={true}
            handleCancel={() => setSelectedJob(null)}
            job={selectedJob}
            handleJobApproval={handleJobApproval}
            handleJobDeny={handleJobDeny}
          />
        )}
        <MobilePanelList<Job>
          data={jobs}
          itemHeight={itemHeight}
          columns={columns.map((c) => ({
            name: c.header,
            searchKey: c.accessorKey,
          }))}
          renderItem={renderItem}
          title={t('job.label_plural', { ns: 'entities' })}
          withSearchBar
          startDate={startDate}
          endDate={endDate}
          setStartDate={handleStartDateChange}
          setEndDate={handleEndDateChange}
          menuOptions={[
            {
              onClick: () => handleCreateClick(),
              content: t('buttons.create', {
                name: t('job.label', { ns: 'entities' }),
              }),
              disabled: !canCreate,
              icon: <Add />,
            },
          ]}
        />
      </>
    );
  } else {
    return (
      <>
        {selectedJob && showDialog === 'cancel' && (
          <CancelJobDialog
            isOpen={true}
            job={selectedJob}
            handleClose={() => {
              setShowDialog(null);
              setSelectedJob(null);
            }}
            cancelReasons={cancelReasonsFetcher.data?.cancelReasons ?? []}
            fetcherKey={fetcherKey}
          />
        )}
        {selectedJob && showDialog !== 'cancel' && (
          <JobReviewDialog
            isOpen={true}
            handleCancel={() => setSelectedJob(null)}
            job={selectedJob}
            handleJobApproval={handleJobApproval}
            handleJobDeny={handleJobDeny}
          />
        )}
        <MaterialReactTable table={table} />
      </>
    );
  }
};

export default JobsPanel;
